import {gql} from '@apollo/client';
import {
  ButtonType,
  CaesarsButton,
  OpenUrlTarget,
  PanelContainer,
  openURL,
  useIsDesktop,
  Selectors,
  useWidgetState,
  useIsWeb,
} from '@eCommerce/frontend-legacy-shared-library-native';
import envData from 'env.json';
import {DateTime} from 'luxon';
import {Box, Text, View} from 'native-base';
import React, {useState, useEffect} from 'react';

import YearDropdown from './YearDropdown';
import {getYears} from './YearDropdown/years';
import {useTheme} from 'styled-components/native';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {isWebViewSelector} from 'store/content';
import {useSelector} from 'Provider';

const {API_URL} = envData;
const BODY_TEXT =
  'Win / Loss Statements for the previous year are available by February 1st of the following year. Win / Loss Statements that are generated prior to February 1st may be incomplete and not include all hand-paid jackpots reportable to the IRS. \n\nThe Gaming History statement shows your net win or loss reflected in our player rating system for the period requested.';
const MAINTENANCE_MESSAGE =
  'Win / Loss statements are currently unavailable due to system maintenance.  Please try again after';
const MAINTENANCE_MESSAGE_NO_TIME =
  'Win / Loss statements are currently unavailable due to system maintenance. Please try again later.';

const getMaintenanceMessage = (timestamp: number | undefined) => {
  if (!timestamp) {
    return MAINTENANCE_MESSAGE_NO_TIME;
  }

  const dateTime = DateTime.fromSeconds(timestamp);
  const dateString = dateTime.toFormat("cccc, LLLL dd, t '('ZZZZZ')'");

  return `${MAINTENANCE_MESSAGE} ${dateString}.`;
};

export interface WinLossStatementsModuleProps {
  maintenance?: {isMaintenance: boolean; maintenanceEnd: number | undefined};
  oToken: string;
}

type FunctionComponentWithFragments<P> = React.FC<P> & {
  fragments: Record<string, any>;
};

const WinLossStatementsModule: FunctionComponentWithFragments<
  WinLossStatementsModuleProps
> = props => {
  const {maintenance, oToken} = props;
  const isDesktop = useIsDesktop();
  const theme = useTheme();
  const {tuna, ghostWhite, caesarRewards} = theme.colors;
  const years = getYears();
  const [selectedYear, setSelectedYear] = useState(years[0]);
  const {trackAdobeEvent} = useTrackEvent();
  const crNumber = useWidgetState(Selectors.userAccountIdSelector) || '';
  const isLoggedIn = Boolean(crNumber);
  const isWebView = useSelector(isWebViewSelector);

  const trackPageView = React.useCallback(() => {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      view: {
        name: 'my account win loss statement',
      },
      rewards: {
        crNumber,
        isLoggedIn,
      },
    });
  }, [trackAdobeEvent, crNumber, isLoggedIn]);

  const trackYearSelection = React.useCallback(() => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'my account win loss statement year selection',
      },
      rewards: {
        crNumber,
        isLoggedIn,
      },
    });
  }, [trackAdobeEvent, crNumber, isLoggedIn]);

  const trackGenerateStatement = React.useCallback(() => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'my account win loss statement generate',
      },
      rewards: {
        crNumber,
        isLoggedIn,
      },
    });
  }, [trackAdobeEvent, crNumber, isLoggedIn]);

  const handleGenerateStatementClick = () => {
    trackGenerateStatement();
    const defaultUrl = `/a/security/winloss.aspx?year=${selectedYear}`;

    if (isWebView) {
      const url = `${API_URL}/asp_net/mobileredirect.aspx?type=redirect&otoken=${oToken}&redirectUrl=${encodeURIComponent(
        defaultUrl
      )}`;
      openURL(url, OpenUrlTarget.BLANK);
      return;
    }

    openURL(defaultUrl, OpenUrlTarget.BLANK);
  };

  // Track page visit
  useEffect(() => {
    trackPageView();
  }, []);

  return (
    <PanelContainer
      defaultOpen={false}
      icon="trophy"
      panelTitle="WIN / LOSS STATEMENTS"
      body={
        maintenance?.isMaintenance
          ? getMaintenanceMessage(maintenance?.maintenanceEnd)
          : BODY_TEXT
      }>
      {maintenance?.isMaintenance ? null : (
        <>
          <View maxW="128px" flexGrow={1}>
            <YearDropdown
              selectYear={(year: string) => {
                setSelectedYear(year);
                trackYearSelection();
              }}
              selectedYear={selectedYear}
              years={years}
            />
          </View>
          <Box h={6} />
          <Box
            bgColor={ghostWhite}
            px={isDesktop ? 6 : 4}
            py={6}
            borderRadius="8px"
            flexDirection="column">
            <Text color={tuna} fontFamily="body" fontSize={2} lineHeight="22px">
              The Caesars Rewards® player rating system is not intended for tax
              reporting purposes to the IRS or any other State or Local
              reporting agency.{'\n\n'}I hereby request that Caesars
              Entertainment provide my historical gaming activity from casinos
              it or any of its subsidiaries or affiliates currently own, operate
              or manage. In consideration for this information, I hereby release
              and hold harmless Caesars Entertainment, its parents, subsidiaries
              and affiliates, and all of their respective officers, directors,
              employees, and agents (“Caesars”) from any and all claims arising
              from or relating to the information or its release, and further
              agree to indemnify and hold harmless Caesars from any such claims.
              I understand that the information is generated from Caesars’
              systems is not and “official record” and does not take the place
              of my own gaming activity records. Caesars makes no representation
              or warranty, express or implied, of the accuracy of the
              information or its effectiveness as proof of wins and losses.
              {'\n\n'}For specific information on tax return preparation and IRS
              requirements, please consult a tax advisor or the IRS at
              www.irs.gov. The IRS recommends that taxpayers keep their own
              diary of their gaming activity with such pertinent information as
              dates, slot machines or table numbers, jackpots, and total wins
              and losses.
              {'\n\n'}
            </Text>
            <Text
              color={tuna}
              fontFamily="body"
              fontWeight="bold"
              fontSize={2}
              lineHeight="22px"
              pb={6}>
              By selecting &quot;Generate Statement&quot; you are accepting the
              above Terms & Conditions
            </Text>
            <View alignItems={isDesktop ? 'flex-start' : 'center'}>
              <View width={isDesktop ? undefined : '100%'}>
                <CaesarsButton
                  title="Generate Statement"
                  type={ButtonType.SECONDARY}
                  color={caesarRewards}
                  isFullWidth={isDesktop ? false : true}
                  onPress={handleGenerateStatementClick}
                />
              </View>
            </View>
          </Box>
        </>
      )}
    </PanelContainer>
  );
};

WinLossStatementsModule.fragments = {
  maintenance: gql`
    fragment WinLossStatementsModuleFragment on MyRewardsKeyValue {
      winLossStatementMaintenance
      winLossStatementMaintenanceEnd {
        timestamp
      }
    }
  `,
};

export default WinLossStatementsModule;
